<template>
  <div class="Order order-details">
    <PageTitle :sub-title="pageTitle" title="Order Details" />

    <!-- Skeleton -->
    <v-skeleton-loader
      v-if="loading"
      class="mb-4 rounded-xl"
      type="list-item-avatar, divider, list-item-three-line, divider, card-heading, list-item-three-line, divider, card-heading, list-item-three-line"
    />

    <div v-else>
      <div class="px-4">
        <!-- Header -->
        <div class="d-flex justify-space-between text-subtitle-2">
          <div class="font-weight-bold my-4">
            Items: {{ orderItems.length }}
          </div>
          <div class="font-weight-medium my-4">
            {{ formatDate(order.purchased_at) }}
          </div>
        </div>

        <v-divider class="mb-4 mt-0" />

        <!-- Order Items -->
        <div
          v-for="(orderItem, idx) in orderItems"
          :key="idx"
          class="d-flex flex-column"
        >
          <div class="d-flex mb-7">
            <v-img
              class="rounded order-details-item-img"
              max-height="63"
              max-width="115"
              :src="getMedia(orderItem)"
              alt="order-detail-media"
              cover
            />

            <div class="d-flex flex-column flex-grow-1 ms-3 text-body-2">
              <div>{{ orderItem.name || '' }}</div>

              <span
                v-if="orderItem.type === 'product' && orderItemHasAttribute(orderItem)"
              >
                {{ orderItem.attribute.name }}
              </span>

              <span>Qty: 1</span>

              <span>
                <v-chip
                  class="mt-2 font-weight-normal px-5 py-3"
                  color="blue"
                  density="compact"
                  label
                  size="small"
                  variant="tonal"
                >
                  <div v-if="orderItem.type === 'product'" class="text-blue">
                    {{
                      orderItem.delivery_method === 'shipped'
                        ? 'Ship'
                        : 'Pickup'
                    }}
                  </div>
                  <div v-else class="text-blue">Instant Download</div>
                </v-chip>
              </span>
            </div>

            <div class="font-weight-bold">
              {{ convertToPoints(orderItem) }}
              pts
            </div>
          </div>

          <!-- Redeemed Alert -->
          <v-alert
            v-if="orderItem.redeemed"
            type="success"
            density="compact"
            class="mb-4 order-details-redeemed-alert"
          >
            This item has been
            {{
              orderItem.delivery_method === 'shipped' ? 'shipped' : 'picked up'
            }}.
          </v-alert>

          <!-- Pickup -->
          <div v-if="shouldShowPickupInstructions(orderItem)">
            <v-btn
              block
              color="primary"
              variant="flat"
              size="large"
              class="order-details-pickup-btn mb-4 font-weight-bold text-decoration-none rounded-pill"
              @click="handlePickup(orderItem)"
            >
              Confirm Pickup
            </v-btn>

            <v-alert
              v-if="validationErrors.length && !pinModalActive"
              type="error"
              variant="outlined"
              density="compact"
              class="mb-4 order-details-redeemed-alert"
            >
              <template #prepend>
                <div class="align-self-start me-4">
                  <icon fill="red" height="20" width="20">
                    exclamation-triangle
                  </icon>
                </div>
              </template>
              <div v-for="(error, i) in validationErrors" :key="i">
                {{ error }}
              </div>
            </v-alert>

            <v-divider class="my-4 divider-line-opacity-10" />

            <div class="font-weight-bold mb-4 text-body-2 text-medium-emphasis">
              Pickup Details:
            </div>

            <div class="text-body-2" v-html="orderItem.pickup_instructions" />
          </div>

          <!-- Shipping -->
          <div
            v-if="orderItem.delivery_method === 'shipped'"
            class="d-flex flex-column justify-space-between"
          >
            <div class="font-weight-bold text-subtitle-2 mb-4 text-medium-emphasis">
              Shipping Address
            </div>

            <div v-if="customerInfo" class="text-body-2">
              <div>{{ customerInfo.first_name }} {{ customerInfo.last_name }}</div>
              <div>{{ customerInfo.address }}</div>
              <div v-if="customerInfo.address_2">{{ customerInfo.address_2 }}</div>
              <div v-if="!customerInfo.address" class="text-red text-body-2">
                Please edit and enter your shipping information
              </div>
              <div>
                <span v-if="order?.customer?.city">{{ customerInfo.city }},</span>
                <span class="ml-1">{{ customerInfo.state }}</span>
                <span>&nbsp;{{ customerInfo.zip_code }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <v-divider color="grey-lighten-2" class="mb-0 mt-7" />

      <div class="py-7">
        <div
          v-if="order.points_paid > 0"
          class="px-4 d-flex justify-space-between text-h6 font-weight-bold"
        >
          <span>Order Points Total</span>
          <span>{{ Number(order.points_paid).toLocaleString() }}</span>
        </div>

        <v-divider
          v-if="order.points_paid > 0 && order.price_paid === '0.00'"          
          class="mx-4 my-3 divider-line-opacity-10"
        />

        <div
          v-if="order.price_paid === '0.00'"
          class="px-4 d-flex justify-space-between text-h6 font-weight-bold"
        >
          <span>Order Cash Total</span>
          <span>${{ order.price_paid }}</span>
        </div>
      </div>

      <v-divider class="mb-7 mt-0" />

      <div class="px-4 pb-7">
        <div class="font-weight-bold text-subtitle-2 mb-4 text-medium-emphasis">
          Payments
        </div>

        <div
          v-if="order.points_paid > 0"
          class="d-flex justify-space-between text-subtitle-2 font-weight-bold"
        >
          <span class="d-flex align-center">
            <icon height="24" width="24">rewards</icon>
            <span class="ms-2">My Rewards</span>
          </span>
          <span>-{{ Number(order.points_paid).toLocaleString() }}</span>
        </div>

        <v-divider class="my-3 opacity-10" />

        <div
          v-if="order.price_paid === '0.00'"
          class="d-flex justify-space-between text-subtitle-2 font-weight-bold"
        >
          <span class="d-flex align-center">
            <icon height="24" width="24">cash</icon>
            <span class="ms-2">Cash</span>
          </span>
          <span>${{ order.price_paid }}</span>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <PinModal
      v-if="pinModalActive"
      :item="validateItem"
      :open="pinModalActive"
      :validation-errors="validationErrors"
      @close-modal="closePinModal"
      @redeem="confirmPickup"
      @set-pin="setPin"
    />

    <PickupCompleteModal
      :show-modal="completePickUpModal"
      @hide-modal="resetPickupModal"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'

dayjs.extend(advancedFormat)
import PageTitle from '@/components/shared/PageTitle/index.vue'
import PinModal from '@/components/modules/Tickets/components/PinModal.vue'
import PickupCompleteModal from './components/PickupCompleteModal.vue'
import $api from '@/api'

export default {
  name: 'OrderDetails',

  components: {
    PageTitle,
    PinModal,
    PickupCompleteModal
  },

  data() {
    return {
      loading: true,
      completePickUpModal: false,
      order: {},
      orderItems: [],
      pageTitle: '',
      pinModalActive: false,
      pinNumber: null,
      validationErrors: [],
      validateItem: null
    }
  },

  computed: {
    ...mapState(['offer', 'partnerUser']),
    ...mapGetters('orders', ['find', 'findOrderItems']),

    customerInfo() {
      const fields = [
        'first_name',
        'last_name',
        'address',
        'address_2',
        'city',
        'state',
        'zip_code'
      ]

      return fields.reduce((acc, key) => {
        acc[key] =
          this.order?.distribute?.[key] ||
          this.order?.customer?.[key] ||
          this.partnerUser?.[key] ||
          ''
        return acc
      }, {})
    }
  },

  async created() {
    const res = await this.getOrderHistory({
      partnerUserId: this.partnerUser.id,
      params: { offerId: this.offer.id, display: 20, page: 1 }
    })

    if (res?.status === 200) {
      this.order = this.find(Number(this.$route.params.orderId)) || {}
      this.orderItems = this.findOrderItems(this.order) || []
      this.pageTitle = this.order.id
        ? `Order #${this.order.id}`
        : 'Loading Order...'
      this.loading = false
    }
  },

  methods: {
    ...mapActions('orders', ['getOrderHistory', 'updateOrderProduct']),

    formatDate(date) {
      return date ? dayjs(date).format('MMMM Do, YYYY') : ''
    },

    getMedia(orderItem) {
      const media = orderItem?.media?.find(m => m.name === 'Hero')
      return (
        media?.url ||
        'https://res.cloudinary.com/bandwango/image/upload/v1651182813/noimage_vsnmt3.webp'
      )
    },

    convertToPoints(item) {
      return item?.points_paid
        ? Number(item.points_paid).toLocaleString()
        : 0
    },

    orderItemHasAttribute(item) {
      return !!item?.attribute?.name
    },

    shouldShowPickupInstructions(item) {
      return !item.redeemed && item.delivery_method === 'picked_up'
    },

    handlePickup(item) {
      this.validateItem = item
      this.validationErrors = []

      this.pinModalActive =
        item.redemption_validation === 'pin' ||
        item.redemption_validation === 'gpsOrPin'

      if (!this.pinModalActive) {
        this.confirmPickup()
      }
    },

    closePinModal() {
      this.pinModalActive = false
      this.validationErrors = []
    },

    setPin(pin) {
      this.validationErrors = []
      this.pinNumber = pin
    },

    async confirmPickup() {
      try {
        const payload = {
          partnerUserId: this.partnerUser.id,
          orderProductIds: [this.validateItem.id],
          pinNumber: this.pinNumber
        }

        const { data } = await $api.products.redeemProduct(
          payload,
          this.$store.state
        )

        if (data.order_products?.length) {
          this.updateOrderProduct(data.order_products[0])
          this.completePickUpModal = true
          this.closePinModal()
        }

        if (data.errors?.length) throw data.errors
      } catch (err) {
        this.validationErrors = Array.isArray(err)
          ? err
          : [err?.message || 'Something went wrong']
      } finally {
        this.pinNumber = null
      }
    },

    resetPickupModal() {
      this.completePickUpModal = false
      this.validateItem = null
    }
  }
}
</script>

<style lang="scss" scoped>
:deep(.v-divider) {
  border-color: rgba(0,0,0, 0.12);
}

:deep(.divider-line-opacity-10) {
  border-color: rgba(0,0,0, 0.5);
}

:deep(.order-details-redeemed-alert) {
  border-inline-start: 6px solid rgba(255, 255, 255, 0.25) !important;
  color: white !important;
  padding: 6px 6px 6px 8px;
}

:deep(.order-details-redeemed-alert .v-icon) {
  font-size: 24px !important;
}

:deep(.v-alert__prepend) {
  margin-inline-end: 14px !important;
}

.order-details-item-img {
  flex-shrink: 0;
  min-height: 63px;
  min-width: 115px;
}

:deep(.v-btn.v-btn--size-large) {
  --v-btn-size: 0.875rem;
  --v-btn-height: 40px;

  font-size: var(--v-btn-size);
  padding: 0 10px;
  border-radius: 2rem !important;
  margin-bottom: 32px !important;
}

/* iOS: first tap can stick as :hover on Vuetify overlay (double-tap to click). */
.order-details-pickup-btn {
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

@media (hover: none) {
  :deep(.order-details-pickup-btn.v-btn:hover > .v-btn__overlay) {
    opacity: 0 !important;
  }
}
</style>